'use client';

import UiIcon from '@/components/ui/UiIcon';
import { memo, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface StackContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onExpand?: () => void;
  onCollapse?: () => void;
  onMerge?: () => void;
  onUngroup?: () => void;
  onCopyAll?: () => void;
  onForwardAsOne?: () => void;
  mode: 'collapsed' | 'expanded' | 'merged';
  stackId: string;
}

// Комментарий на русском: контекстное меню для управления стэком (long-press / клик правой кнопкой).
const StackContextMenu = memo(function StackContextMenu(props: StackContextMenuProps) {
  const {
    isOpen,
    onClose,
    onExpand,
    onCollapse,
    onMerge,
    onUngroup,
    onCopyAll,
    onForwardAsOne,
    mode,
  } = props;

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const menuItems = [];

  if (mode === 'collapsed') {
    menuItems.push(
      { label: 'Развернуть', icon: '📂', onClick: onExpand },
      { label: 'Слить в одно', icon: '🔀', onClick: onMerge },
      { label: 'Разгруппировать', icon: '💥', onClick: onUngroup }
    );
  }

  if (mode === 'expanded') {
    menuItems.push(
      { label: 'Свернуть', icon: '📁', onClick: onCollapse },
      { label: 'Слить в одно', icon: '🔀', onClick: onMerge },
      { label: 'Разгруппировать', icon: '💥', onClick: onUngroup }
    );
  }

  if (mode === 'merged') {
    menuItems.push(
      { label: 'Разделить обратно', icon: '✂️', onClick: onCollapse }
    );
  }

  menuItems.push(
    { label: 'Скопировать всё', icon: '📋', onClick: onCopyAll },
    { label: 'Переслать как одно', icon: '➡️', onClick: onForwardAsOne }
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center sm:justify-center"
      onClick={handleBackdropClick}
    >
      {/* Комментарий на русском: backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      
      {/* Комментарий на русском: меню (bottom sheet на мобилке, popover на десктопе) */}
      <div
        className="relative w-full sm:w-auto sm:min-w-[280px] bg-site-surface border border-site-border rounded-t-3xl sm:rounded-2xl p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Комментарий на русском: handle для свайпа вниз (только на мобилке) */}
        <div className="flex justify-center mb-3 sm:hidden">
          <div className="w-12 h-1 rounded-full bg-site-raised" />
        </div>

        <div className="text-sm font-medium text-site-secondary mb-3">Действия со стэком</div>

        <div className="space-y-1">
          {menuItems.map((item, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => {
                item.onClick?.();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-site-raised/50 transition text-left text-site-text"
            >
              <span className="text-xl">{<UiIcon name={item.icon} />}</span>
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full mt-4 py-3 rounded-xl bg-site-raised/50 hover:bg-site-raised transition text-site-text text-sm font-medium"
        >
          Закрыть
        </button>
      </div>
    </div>,
    document.body
  );
});

export default StackContextMenu;
